import { useEffect, useRef, useState } from 'react';
import SectionCard from '@/components/base/SectionCard';
import Button from '@/components/base/Button';
import type { AssessmentTone, Creator } from '@/types/portal';
import { creatorAssessments } from '@/mocks/vetting';

type Phase = 'idle' | 'reviewing' | 'done';

const tonePill: Record<AssessmentTone, string> = {
  positive: 'bg-accent-100 text-accent-800 border-accent-200',
  caution: 'bg-secondary-100 text-secondary-900 border-secondary-200',
  negative: 'bg-primary-100 text-primary-800 border-primary-200',
};

interface AiAssessmentPanelProps {
  creator: Creator;
}

export default function AiAssessmentPanel({ creator }: AiAssessmentPanelProps) {
  const [phase, setPhase] = useState<Phase>('idle');
  const timerRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    },
    [],
  );

  const assessment = creatorAssessments[creator.id];

  const signals = [
    { icon: 'ri-links-line', label: `${creator.platforms.length} connected platforms` },
    {
      icon: 'ri-shield-user-line',
      label: `${creator.verification} · ${creator.kycStatus}`,
    },
    { icon: 'ri-wallet-3-line', label: creator.walletStatus },
    {
      icon: 'ri-history-line',
      label: `${creator.agencyHistory.campaigns} campaigns · ${creator.agencyHistory.onTimeRate}% on time`,
    },
    {
      icon: 'ri-group-line',
      label: `${creator.audience.women}% women · ${creator.audience.topAge}`,
    },
    { icon: 'ri-shield-check-line', label: `Brand safety: ${creator.brandSafety}` },
  ];

  const runVet = () => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    setPhase('reviewing');
    timerRef.current = window.setTimeout(() => setPhase('done'), 1600);
  };

  return (
    <SectionCard
      title="AI assessment"
      subtitle="Automated pre-contract review of this creator"
      action={
        <Button
          variant="outline"
          size="sm"
          icon="ri-shield-check-line"
          onClick={runVet}
          disabled={phase === 'reviewing'}
        >
          {phase === 'idle' ? 'Vet this creator' : phase === 'reviewing' ? 'Reviewing…' : 'Re-vet'}
        </Button>
      }
    >
      {phase === 'idle' ? (
        <div>
          <p className="max-w-3xl text-sm leading-relaxed text-foreground-600">
            Ask the assistant to review everything the platform knows about{' '}
            <span className="font-medium text-foreground-800">{creator.name}</span> — connected
            platforms, audience quality, delivery history and compliance — before you commit to a
            contract.
          </p>

          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {signals.map((signal) => (
              <div
                key={signal.label}
                className="flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-3 py-2"
              >
                <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                  <i className={signal.icon} />
                </span>
                <span className="text-xs text-foreground-600">{signal.label}</span>
              </div>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <p className="inline-flex items-center gap-1.5 text-[11px] text-foreground-400">
              <i className="ri-time-line" />
              Runs in seconds · nothing is shared with the creator
            </p>
            <Button size="sm" icon="ri-shield-check-line" onClick={runVet}>
              Vet this creator
            </Button>
          </div>
        </div>
      ) : null}

      {phase === 'reviewing' ? (
        <div className="flex flex-col items-center justify-center gap-3 py-8 text-center">
          <span className="w-11 h-11 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 text-lg">
            <i className="ri-loader-4-line animate-spin" />
          </span>
          <p className="text-sm font-medium text-foreground-800">
            Reviewing {signals.length} signals…
          </p>
          <p className="max-w-sm text-xs leading-relaxed text-foreground-500">
            Cross-checking connected platforms, audience quality, delivery history and compliance.
            This usually takes a few seconds.
          </p>
        </div>
      ) : null}

      {phase === 'done' && assessment ? (
        <div>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <span
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-label font-semibold ${tonePill[assessment.tone]}`}
            >
              <i className="ri-shield-check-line" />
              {assessment.verdict}
            </span>
            <span
              className="flex items-center gap-0.5"
              aria-label={`${assessment.stars} out of 5`}
            >
              {[1, 2, 3, 4, 5].map((star) => (
                <span key={star} className="w-4 h-4 flex items-center justify-center text-base">
                  <i
                    className={
                      star <= assessment.stars
                        ? 'ri-star-fill text-accent-500'
                        : 'ri-star-line text-foreground-300'
                    }
                  />
                </span>
              ))}
            </span>
          </div>

          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-700">
            {assessment.summary}
          </p>

          <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
            <div>
              <p className="mb-2 text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">
                Strengths
              </p>
              <ul className="space-y-1.5">
                {assessment.strengths.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-foreground-700">
                    <i className="ri-check-line mt-0.5 shrink-0 text-accent-600" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="mb-2 text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">
                Risks
              </p>
              <ul className="space-y-1.5">
                {assessment.risks.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-foreground-700">
                    <i className="ri-error-warning-line mt-0.5 shrink-0 text-secondary-600" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center justify-between gap-2 border-t border-background-100 pt-3">
            <p className="inline-flex items-center gap-1.5 text-[11px] text-foreground-400">
              <i className="ri-robot-2-line" />
              Powered by {assessment.model}
            </p>
            <button
              type="button"
              onClick={runVet}
              className="inline-flex items-center gap-1 text-[11px] font-label text-foreground-500 cursor-pointer hover:text-foreground-800"
            >
              <i className="ri-refresh-line" />
              Re-run review
            </button>
          </div>
        </div>
      ) : null}
    </SectionCard>
  );
}